/**
 * <asd-milestone-reward> silently adds/removes the hidden "Sticker Pack" reward product to the
 * cart as the customer crosses (or drops below) Milestone bar tier 1. Shopify's automatic
 * "Milestone Tier 1 - Free sticker pack at $75" discount (Shopify admin > Discounts) is what makes
 * the line free once it's in the cart - a Buy X Get Y discount never adds the "get" item itself.
 *
 * Threshold check ignores the reward line's own price, so the reward can never pay for its own
 * inclusion (mirrors how the tier-2 free shirt only counts the customer's OTHER items).
 */
class AsdMilestoneReward extends HTMLElement {
  #busy = false;
  #dirty = false;

  connectedCallback() {
    this.variantId = this.dataset.variantId;
    // In the shop's currency (CAD) - Liquid can't read the buyer's exchange rate, only
    // window.Shopify.currency.rate does, client-side. See #threshold below.
    this.thresholdShopCurrency = Number(this.dataset.thresholdShopCurrency) || 0;

    document.addEventListener('shopify:cart:lines-update', this.#onCartEvent);
    document.addEventListener('shopify:cart:discount-update', this.#onCartEvent);
    window.addEventListener('pageshow', this.#onPageShow);

    this.#refresh();
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this.#onCartEvent);
    document.removeEventListener('shopify:cart:discount-update', this.#onCartEvent);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#refresh();
  };

  /** @param {Event & { promise?: Promise<unknown> }} event */
  #onCartEvent = (event) => {
    Promise.resolve(event.promise)
      .catch(() => {})
      .then(() => this.#refresh());
  };

  get #cartUrl() {
    return window.Theme?.routes?.cart_url ?? '/cart';
  }

  async #refresh() {
    if (this.#busy) {
      this.#dirty = true;
      return;
    }
    this.#busy = true;
    try {
      await this.#reconcile();
    } finally {
      this.#busy = false;
      if (this.#dirty) {
        this.#dirty = false;
        this.#refresh();
      }
    }
  }

  // Fixed, approximate CAD-per-USD rate used only to convert the international threshold into the
  // buyer's local currency (not live-fetched - see #threshold). Update this if it drifts a lot.
  static #CAD_PER_USD = 1.37;

  /**
   * thresholdShopCurrency is entered by the merchant in CAD. Canada sees that number exactly.
   * Everyone else sees the SAME number in USD instead: exact for the US market, and for every
   * other currency, converted from USD and rounded to a whole unit - international thresholds
   * don't need to land on an exact converted CAD figure.
   */
  #threshold() {
    const raw = this.thresholdShopCurrency; // CAD cents
    const active = window.Shopify?.currency?.active;
    if (!active || active === 'CAD' || active === 'USD') return raw;

    const cadToActive = Number(window.Shopify?.currency?.rate);
    if (!Number.isFinite(cadToActive) || cadToActive <= 0) return raw;

    const usdToActive = cadToActive * AsdMilestoneReward.#CAD_PER_USD;
    return Math.round((raw * usdToActive) / 100) * 100; // nearest whole unit, no cents
  }

  async #reconcile() {
    const cart = await this.#getCart();
    if (!cart) return;

    const rewardLine = cart.items.find((item) => String(item.variant_id) === String(this.variantId));
    const otherSubtotal = cart.items_subtotal_price - (rewardLine ? rewardLine.line_price : 0);
    const threshold = this.#threshold();
    const qualifies = otherSubtotal >= threshold;

    if (qualifies && !rewardLine) {
      await this.#post('/cart/add.js', { id: String(this.variantId), quantity: 1 });
      this.#notify();
    } else if (!qualifies && rewardLine) {
      await this.#post('/cart/change.js', { id: String(this.variantId), quantity: 0 });
      this.#notify();
    }
  }

  async #getCart() {
    try {
      const response = await fetch(`${this.#cartUrl}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  async #post(url, body) {
    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
    } catch {
      // Next cart event (or the next page load) will retry.
    }
  }

  /** Let the rest of the theme (milestone bar, cart count) know the cart changed under it. */
  #notify() {
    document.dispatchEvent(
      new CustomEvent('shopify:cart:lines-update', { bubbles: true, promise: Promise.resolve() }),
    );
  }
}

if (!customElements.get('asd-milestone-reward')) {
  customElements.define('asd-milestone-reward', AsdMilestoneReward);
}
